import { cn } from "@breenova/ui";
import { Bird } from "lucide-react";
import type { ReactNode } from "react";

/** The three claims the marketing panel makes. Facts about the app, not slogans. */
const PITCH: readonly { figure: string; label: string }[] = [
  { figure: "5 gen", label: "Pedigrees" },
  { figure: "Offline", label: "In the aviary" },
  { figure: "CSV", label: "Your data, yours" },
];

/**
 * The frame every signed-out screen sits in.
 *
 * On a phone: bottom-anchored and left-aligned rather than centred, so the fields end up
 * under the thumb instead of in the middle of the screen, and the column reads as the
 * start of something rather than as a modal.
 *
 * From `lg` it splits. A 400px form centred in a 1440px window is a small box in a large
 * empty room — the window is not a bigger phone, it is a different shape, and the half
 * the form does not need goes to saying what this is. The panel is deep green: the one
 * place in the app a large saturated fill is right, because nothing on it is a record.
 */
export function AuthLayout({
  title,
  subtitle,
  wideTitle,
  wideSubtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  /**
   * What the heading says from `lg`, where the hero panel beside the form already names
   * the app — so the phone can lead with "BreeNova" and the desktop with "Welcome back".
   */
  wideTitle?: string | undefined;
  wideSubtitle?: string | undefined;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh bg-surface lg:items-stretch">
      <HeroPanel />

      <main
        className={cn(
          "relative flex min-h-dvh flex-1 flex-col overflow-hidden bg-surface",
          /*
           * Bottom-anchored on a phone, where the form should meet the thumb and the
           * keyboard rises to meet it. Centred from `md`, where "at the bottom" means an
           * inch above the taskbar with two thirds of the window empty above it.
           */
          "justify-end md:justify-center",
        )}
      >
        {/*
          The two bleed circles are the only decoration in the app. They are `aria-hidden`
          and clipped by the frame, so they cost nothing to a screen reader and nothing to
          layout. From `lg` the hero panel carries the decoration and these would be a
          second, competing set of circles on the same screen.
        */}
        <span
          aria-hidden
          className="pointer-events-none absolute -right-30 -top-22.5 size-85 rounded-full bg-primary-fixed lg:hidden"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -left-22.5 top-50 size-50 rounded-full bg-secondary-fixed lg:hidden"
        />

        <div
          className={cn(
            "relative mx-auto flex w-full max-w-form flex-col gap-5.5",
            "px-6 pb-[calc(var(--spacing)*10+var(--safe-area-bottom))]",
            // Same reason as the shell: installed on iOS this draws under the status bar.
            "pt-[calc(var(--spacing-xl)+var(--safe-area-top))]",
            // 400px of fields, 26px apart, in the middle of what the panel leaves.
            "lg:max-w-auth-form lg:gap-xl lg:px-0 lg:pb-0 lg:pt-0",
          )}
        >
          <header className="flex flex-col gap-5.5">
            {/* The mark is the app saying its own name. Beside the hero panel, which
                already does, it would be the second wordmark on one screen. */}
            <span
              aria-hidden
              className="flex size-15.5 items-center justify-center rounded-full bg-primary text-on-primary lg:hidden"
            >
              <Bird className="size-7" />
            </span>

            <div className="flex flex-col gap-1.5">
              <h1 className="text-hero text-on-surface lg:hidden">{title}</h1>
              <h1 className="hidden text-auth-title text-on-surface lg:block">
                {wideTitle ?? title}
              </h1>
              {subtitle ? (
                // Capped at about thirty characters a line: the eye finds the next line
                // without hunting, which matters more here than filling the width.
                <p className="max-w-[30ch] text-tagline text-on-surface-body lg:hidden">{subtitle}</p>
              ) : null}
              {wideSubtitle ?? subtitle ? (
                <p className="hidden max-w-[30ch] text-lead text-on-surface-variant lg:block">
                  {wideSubtitle ?? subtitle}
                </p>
              ) : null}
            </div>
          </header>

          {children}

          {footer}
        </div>
      </main>
    </div>
  );
}

/**
 * What BreeNova is, for the half of a desktop window the form does not need.
 *
 * Absent below `lg` rather than stacked above the form: on a phone this is a screen of
 * marketing between the breeder and the password field they came to type.
 */
function HeroPanel() {
  return (
    <aside
      className={cn(
        "relative hidden w-hero-panel shrink-0 overflow-hidden lg:flex",
        "flex-col justify-between bg-primary-container p-hero",
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-36 size-118 rounded-full bg-accent-mark-soft"
      />

      <div className="relative flex items-center gap-md">
        <span
          aria-hidden
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed"
        >
          <Bird className="size-6" />
        </span>
        <span className="text-auth-title text-on-primary-container">BreeNova</span>
      </div>

      <div className="relative flex flex-col gap-lg">
        <p className="max-w-[20ch] text-stat text-on-primary-container text-pretty">
          Every ring, every clutch, every season.
        </p>

        <p className="max-w-[36ch] text-lead-wide text-on-primary-container text-pretty">
          Canaries and European goldfinches — pedigrees that go back five generations,
          incubation you can trust, and a health log that survives an inspection.
        </p>

        <dl className="flex gap-2xl pt-xs">
          {PITCH.map((item) => (
            <div key={item.label} className="flex flex-col gap-px">
              <dt className="text-pitch text-inverse-primary" data-numeric>
                {item.figure}
              </dt>
              <dd className="text-nav-label uppercase text-on-primary-container">{item.label}</dd>
            </div>
          ))}
        </dl>
      </div>

      <p className="relative text-cell text-on-primary-container">
        Built with Dutch and Belgian breeders · NBvV and AOB ring formats
      </p>
    </aside>
  );
}
